import GRPCLuna from "./GRPCLuna";
import {
    EudForgotUsernameRequest,
    EudLiteResponse,
    EudLoginRequest, EudLunaToken,
    EudRefreshRequest, EudResetPasswordRequest,
    EudTokenRefreshResponse,
    EudTokenResponse
} from "@ev24/proto-eud-luna-web-api/elwa_v1_pb";
import type {Reject, Resolve} from "../utility/InterfacePromise";
import type {RpcError} from "grpc-web";
import useAuthStore, {TOKEN_NAMES} from "../store/AuthStore";
import type {GRPCHandler} from "./GRPCLuna";
import useAlertStore from "../store/AlertStore";
import type {AuthInfo} from "../interfaces/AuthInterface";


export default class LunaLogin extends GRPCLuna {
    public async login(username: string, password: string, handler: GRPCHandler | null = null) {

        const UUID = this.getUuid();
        const request: EudLoginRequest = new EudLoginRequest();

        request.setRequestId(UUID);
        request.setUsername(username);
        request.setPassword(password);

        return new Promise((resolve: Resolve, reject: Reject): void => {

            const clientService = this.userAccountClient();
            clientService.login(
                request,
                this._headers,
                (err: RpcError | null, response: EudTokenRefreshResponse | null) => {
                    if(!handler) {
                        this.loginHandler(response, err, resolve, reject);
                    } else {
                        handler(response, err, resolve, reject);
                    }
                }
            );
        });
    }

    protected loginHandler( response: EudTokenRefreshResponse | null, err: RpcError | null, resolve: Resolve, reject: Reject) {
        const authStore = useAuthStore();
        const alertStore = useAlertStore();
        const responseData = this.responseData(
            <EudTokenRefreshResponse>response
        );
        if (responseData === null) {
            reject(err);
            return;
        }
        if (responseData.code === "200") {
            alertStore.success("Welcome to Evolve24");
            authStore.setRefreshCode(responseData.refreshCode);
            authStore.setAuthInfo(this.createAuthInfo(
                responseData,
                authStore.authInfo
            ));
            resolve(authStore.authInfo);
        } else if (responseData.code) {
            reject(responseData.message);
        } else {
            const message = err?.message || "Server Error: L101";
            alertStore.error(message);
            console.error(err);
            reject(err);
        }
    }

    public async forgotUsername(email: string, handler: GRPCHandler | null = null) {
        const clientService = this.userAccountClient();

        const request: EudForgotUsernameRequest = new EudForgotUsernameRequest();
        request.setEmail(email);
        return new Promise((resolve, reject): void => {
            clientService.forgotUsername(
                request,
                this._headers,
                (err: RpcError, response: EudLiteResponse) => {
                    if(!handler) {
                        this.forgotUsernameHandler(response, err, resolve, reject);
                    } else {
                        handler(response, err, resolve, reject);
                    }
                }
            );
        });
    }

    protected forgotUsernameHandler(response: EudLiteResponse | null, err: RpcError | null, resolve: Resolve, reject: Reject) {
        const alertStore = useAlertStore();
        const responseData = this.responseData(<EudLiteResponse>response);
        if (responseData === null || response === null) {
            reject(err);
            return;
        }
        if (responseData.code === "200") {
            alertStore.success(responseData.message);
            resolve(response.toObject());
        } else if (responseData.code) {
            alertStore.error(responseData.message);
            reject(responseData.message);
        } else {
            const message = err?.message || "Server Error: FO101";
            alertStore.error(message);
            console.error(err);
            reject(err);
        }
    }

    public async passwordReset(username: string, handler: GRPCHandler | null = null) {
        const clientService = this.userAccountClient();

        const request: EudResetPasswordRequest = new EudResetPasswordRequest();
        request.setUsername(username);
        return new Promise((resolve, reject): void => {
            clientService.resetPassword(
                request,
                this._headers,
                (err: RpcError, response: EudLiteResponse) => {
                    if(!handler) {
                        this.passwordResetHandler(response, err, resolve, reject);
                    } else {
                        handler(response, err, resolve, reject);
                    }
                }
            );
        });
    }

    protected passwordResetHandler(response: EudLiteResponse | null, err: RpcError | null, resolve: Resolve, reject: Reject) {
        const alertStore = useAlertStore();
        const responseData = this.responseData(<EudLiteResponse>response);
        if (responseData === null || response === null) {
            reject(err);
            return;
        }
        if (responseData.code === "200") {
            alertStore.success(responseData.message);
            resolve(response.toObject());
        } else if (responseData.code) {
            alertStore.error(responseData.message);
            reject(responseData.message);
        } else {
            const message = err?.message || "Server Error: PR101";
            alertStore.error(message);
            console.error(err);
            reject(err);
        }
    }

    public async refreshToken(refreshCode: string, handler: GRPCHandler | null = null) {
        if (!refreshCode) {
            return new Promise((resolve, reject): void => {
                reject("No refresh token found, skipping refresh check.");
            });
        }
        const UUID = this.getUuid();
        const clientService = this.userAccountClient();
        const request: EudRefreshRequest = new EudRefreshRequest();

        request.setRequestId(UUID);
        request.setRefreshCode(refreshCode);

        return new Promise((resolve, reject): void => {
            clientService.refresh(
                request,
                this._headers,
                (err: RpcError | null, response: EudTokenResponse | null) => {
                    if(!handler) {
                        this.refreshTokenHandler(response, err, resolve, reject);
                    } else {
                        handler(response, err, resolve, reject);
                    }
                }
            );
        });
    }

    protected refreshTokenHandler(response: EudTokenResponse | null, err: RpcError | null, resolve: Resolve, reject: Reject) {
        const authStore = useAuthStore();
        const responseData = this.responseData(
            <EudTokenResponse>response
        );
        if (responseData === null) {
            reject(err);
            return;
        }
        if (responseData.code === "200") {
            authStore.setAuthInfo(this.createAuthInfo(
                responseData,
                authStore.authInfo
            ));
            authStore.updateRefreshCookie();
            resolve(authStore.authInfo);
        } else if (responseData.code) {
            reject(responseData.message);
        } else {
            console.error(err);
            reject(err);
        }
    }

    protected createAuthInfo(
        responseData: EudTokenRefreshResponse.AsObject | EudTokenResponse.AsObject,
        authInfo: AuthInfo
    ): AuthInfo {
        responseData.tokensList.forEach((token: EudLunaToken.AsObject) => {
            const tokenName = token.id as keyof AuthInfo;
            if (TOKEN_NAMES.includes(tokenName)) {
                authInfo[tokenName] = token.accessToken;
            }
        });
        return authInfo;
    }

}